/**
 * PR Cost Guardrail — admin dashboard for reviewing past evaluations and
 * grabbing the GitHub Action snippet.
 *
 * The killer surface is the math breakdown: every evaluation shows the
 * explicit engineer-hour calculation that justified ALLOW / WARN / BLOCK.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  ShieldCheck, GitPullRequest, Copy, Code, ChevronRight, Calculator,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const map = {
    allow: { bg: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300', label: 'ALLOW' },
    warn:  { bg: 'bg-amber-500/15 border-amber-500/30 text-amber-300', label: 'WARN' },
    block: { bg: 'bg-rose-500/15 border-rose-500/30 text-rose-300', label: 'BLOCK' },
  };
  const s = map[status] || map.warn;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest border ${s.bg}`}>
      {s.label}
    </span>
  );
};

export default function PRCostGuardrail() {
  const [yaml, setYaml] = useState('');
  const [copied, setCopied] = useState(false);
  const [demoEval, setDemoEval] = useState(null);
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoFields, setDemoFields] = useState({
    pr_title: 'Add a Redshift cluster for analytics',
    predicted_cost_delta_monthly_usd: 8000,
    engineer_hours_estimate: 12,
    affected_customer_count: 0,
    declared_value_usd_per_customer_monthly: 0,
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API}/api/pr-guardrail/github-action-config`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setYaml(d.content || ''))
      .catch(() => {});
  }, []);

  const runDemo = async () => {
    setDemoBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/pr-guardrail/evaluate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(demoFields),
      });
      setDemoEval(await r.json());
    } finally {
      setDemoBusy(false);
    }
  };

  const copyYaml = () => {
    navigator.clipboard.writeText(yaml);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 -mt-px"><div className="max-w-6xl mx-auto px-6 py-8" data-testid="pr-cost-guardrail-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="inline-flex w-12 h-12 rounded-xl bg-emerald-500 bg-gradient-to-br from-emerald-500 to-teal-500 items-center justify-center shadow-lg shadow-cyan-500/30">
            <ShieldCheck className="w-6 h-6 text-white drop-shadow-sm" />
          </div>
          <div>
            <Badge className="bg-emerald-500/15 border border-emerald-500/30 text-cyan-300 text-[10px] font-mono tracking-widest mb-1">WORLD-FIRST</Badge>
            <h1 className="text-3xl font-bold text-slate-900">PR Cost Guardrail · Engineer-Hour Math</h1>
          </div>
        </div>
        <p className="text-base text-slate-700 max-w-3xl leading-relaxed">
          The only Terraform/CDK guard that <span className="text-white">blocks PRs based on engineer-hour ROI math</span>, not just dollar thresholds. Harness has $ gates. We have value-cost ratios.
        </p>
      </div>

      {/* Live demo */}
      <Card className="bg-slate-950 text-slate-100 border-emerald-500/20 mb-8" data-testid="guardrail-demo-card">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Calculator className="w-4 h-4 text-cyan-400" />
            Try it on a sample PR
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-300 uppercase tracking-widest mb-1 block font-semibold">PR title</label>
                <input
                  type="text"
                  value={demoFields.pr_title}
                  onChange={(e) => setDemoFields({ ...demoFields, pr_title: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
                  data-testid="demo-pr-title"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-300 uppercase tracking-widest mb-1 block font-semibold">$ / month delta</label>
                  <input
                    type="number"
                    value={demoFields.predicted_cost_delta_monthly_usd}
                    onChange={(e) => setDemoFields({ ...demoFields, predicted_cost_delta_monthly_usd: Number(e.target.value) })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-300 uppercase tracking-widest mb-1 block font-semibold">Engineer hours</label>
                  <input
                    type="number"
                    value={demoFields.engineer_hours_estimate}
                    onChange={(e) => setDemoFields({ ...demoFields, engineer_hours_estimate: Number(e.target.value) })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-300 uppercase tracking-widest mb-1 block font-semibold">Customers affected</label>
                  <input
                    type="number"
                    value={demoFields.affected_customer_count}
                    onChange={(e) => setDemoFields({ ...demoFields, affected_customer_count: Number(e.target.value) })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-300 uppercase tracking-widest mb-1 block font-semibold">$/customer/month value</label>
                  <input
                    type="number"
                    value={demoFields.declared_value_usd_per_customer_monthly}
                    onChange={(e) => setDemoFields({ ...demoFields, declared_value_usd_per_customer_monthly: Number(e.target.value) })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
                  />
                </div>
              </div>
              <Button
                onClick={runDemo}
                disabled={demoBusy}
                className="w-full bg-cyan-600 hover:bg-emerald-500 text-white"
                data-testid="run-guardrail-demo"
              >
                {demoBusy ? 'Evaluating…' : 'Run guardrail'}
              </Button>
            </div>
            <div>
              {demoEval ? (
                <div className="rounded-lg border border-slate-700 bg-slate-950 p-4 h-full">
                  <div className="flex items-start justify-between mb-3">
                    <div className="text-sm font-bold text-white pr-2">{demoEval.title}</div>
                    <StatusBadge status={demoEval.status} />
                  </div>
                  <p className="text-xs text-slate-400 whitespace-pre-wrap leading-relaxed mb-3">{demoEval.summary}</p>
                  <div className="border-t border-slate-700 pt-3">
                    <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mb-2">Math (auditable)</div>
                    <div className="space-y-1 font-mono text-xs">
                      {Object.entries(demoEval.math || {}).map(([k, v]) => (
                        <div key={k} className="flex justify-between text-slate-400">
                          <span>{k}</span>
                          <span className="text-slate-300">{typeof v === 'number' ? v.toLocaleString() : String(v)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-slate-700 bg-slate-950 p-6 h-full flex flex-col items-center justify-center text-center">
                  <GitPullRequest className="w-8 h-8 text-slate-400 mb-3" />
                  <p className="text-sm text-slate-400">Adjust inputs on the left and click <span className="text-cyan-300">Run guardrail</span> to see the math.</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* GitHub Action snippet */}
      <Card className="bg-slate-950 text-slate-100 border-emerald-500/20">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Code className="w-4 h-4 text-cyan-400" />
              Drop this into <code className="text-cyan-300 text-sm">.github/workflows/</code>
            </span>
            <Button size="sm" onClick={copyYaml} className="bg-emerald-700 hover:bg-emerald-600 text-white text-xs">
              <Copy className="w-3 h-3 mr-1" />{copied ? 'Copied' : 'Copy YAML'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="bg-slate-900 border border-emerald-500/20 rounded-md p-4 overflow-x-auto text-xs text-slate-300 font-mono leading-relaxed">{yaml || 'Loading…'}</pre>
          <p className="text-xs text-slate-500 mt-3 flex items-start gap-1">
            <ChevronRight className="w-3 h-3 mt-0.5" />
            Set <code className="text-slate-400">CARTIE_API_BASE</code> and <code className="text-slate-400">CARTIE_API_KEY</code> as repo secrets.
          </p>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
